<template>
  <div
    ref="target"
    :style="styles"
    class="auto-complete"
    :class="{
      'auto-complete--is-open': isOpen,
      'auto-complete--is-visible': isVisible,
      'auto-complete--is-full-width': fullWidth,
    }"
  >
    <div
      v-if="isVisible"
      class="ac-container"
    >
      <div class="ac-header">
        <span class="ac-title">
          {{ $t('autoComplete.title') }}
        </span>

        <fn-button
          v-if="showClose"
          class="ac-close"
          icon="cross"
          icon-size="xs"
          icon-center
          plain
          @click="close"
        />
      </div>

      <div class="ac-results">
        <fn-loading-overlay v-if="isLoading" />

        <fn-list unstyled>
          <li
            v-for="suggestion in suggestionsHighlighted"
            :key="suggestion.id"
            class="ac-result"
            @click="selectSuggestion(suggestion)"
            v-html="suggestion.highlighted"
          />

          <li
            v-if="!suggestionsHighlighted.length"
            class="ac-result ac-result--empty"
            v-html="$t('autoComplete.noResults')"
          />
        </fn-list>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { onClickOutside } from '@vueuse/core';
import { debounce } from '@/utils/debounce';
import { sanitizeRegex } from '@/utils/sanitizer';

export default {
  inject: {
    $_autoCompleteObserver: {
      from: '$_autoCompleteObserver',
      default() {
        return null;
      },
    },
  },
  props: {
    id: {
      type: String,
      default: '',
    },
    value: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: '',
    },
    focused: {
      type: Boolean,
      required: true,
    },
    validationProvider: {
      type: Object,
      default: null,
    },
    debounceDuration: {
      type: Number,
      default: 300,
    },
    fullWidth: {
      type: Boolean,
      default: true,
    },
    showClose: {
      type: Boolean,
      default: true,
    },
    closeOnBlur: {
      type: Boolean,
      default: true,
    },
  },
  setup(props) {
    const target = ref(null);

    onClickOutside(target, (event) => {
      if (props.closeOnBlur && !event.target.closest('.form-field')?.contains(this.$el)) {
        this.close();
      }
    });

    return {};
  },
  data: () => ({
    suggestions: [],
    isOpen: false,
    isLoading: false,
    resizeObserver: null,
    top: 0,
    left: 0,
    width: 0,
  }),
  computed: {
    ...mapState(useAutocompleteStore, {
      getAutoCompleteEnabled: 'getAutoCompleteEnabled',
    }),

    autoCompleteEnabled() {
      return this.getAutoCompleteEnabled && this.type;
    },

    isVisible() {
      return this.isOpen && (this.isLoading || this.suggestions?.length);
    },

    styles() {
      if (!this.fullWidth) {
        return {};
      }

      return {
        top: `${this.top}px`,
        left: `${this.left}px`,
        width: `${this.width}px`,
      };
    },

    suggestionsHighlighted() {
      if (!this.suggestions?.length) {
        return [];
      }

      return this.suggestions.map((suggestion) => ({
        ...suggestion,
        highlighted: this.getSuggestionHighlighted(suggestion),
      }));
    },

    getResultsDebounced() {
      return debounce(async () => {
        if (!this.isOpen) {
          return;
        }

        this.isLoading = true;

        try {
          const searchParams = {
            address: this.value,
            addressType: 'ADDRESS_LINE',
          };

          const searchValues = this.$_autoCompleteObserver?.searchValues;

          if (searchValues?.length === 1) {
            searchParams.address = searchValues[0].value;
            searchParams.addressType = searchValues[0].type;
          } else {
            searchParams.address = searchValues?.map((val) => val.value).join(' ');
          }

          this.suggestions = await this.getResults(searchParams);
        } catch (e) {
          this.suggestions = [];
        }

        this.isLoading = false;
      }, this.debounceDuration);
    },
  },
  mounted() {
    if (!this.autoCompleteEnabled) {
      return;
    }

    this.$_autoCompleteObserver?.register(this);

    this.$watch(
      'focused',
      (newValue) => {
        if (newValue) {
          this.$_autoCompleteObserver?.onFocus(this);
        }
      },
      { immediate: true },
    );

    this.$watch(
      'value',
      () => {
        this.getResultsDebounced();
      },
      { immediate: true },
    );
  },
  beforeUnmount() {
    this.$_autoCompleteObserver?.unregister(this);
  },
  methods: {
    ...mapActions(useAutocompleteStore, {
      retrieveToken: 'retrieveToken',
      getResults: 'getResults',
    }),

    open() {
      this.retrieveToken();
      this.isOpen = true;
      this.suggestions = [];
    },

    close() {
      if (this.isOpen) {
        this.validationProvider?.validate();
        this.getResultsDebounced.clear();
        this.isOpen = false;
        this.isLoading = false;
      }
    },

    selectSuggestion(suggestion) {
      this.$_autoCompleteObserver?.setSuggestion(suggestion);
      this.close();
    },

    getSuggestionHighlighted(address) {
      let addressSuggestion = `${[address.line1, address.line2].join(' ')}, ${address.postalCode} ${address.town}, ${
        address.country.name
      }`;

      // const suggestionString = [
      //   [address.line1, address.line2].join(' '),
      //   [address.address.postalCode, address.town].join(' '),
      //   address.country.name
      // ].filter(item => item).join(', ');

      // Get autocomplete match values
      const autocompleteMatchValues = this.$_autoCompleteObserver?.searchValues;

      // Highlight autocomplete match value if it exists anywhere in the address string
      for (const { value } of autocompleteMatchValues) {
        const regex = new RegExp(`(${sanitizeRegex(value)})`, 'i');
        addressSuggestion = addressSuggestion.replace(regex, '<b>$1</b>');
      }

      // Prevent v-html from removing spaces between HTML nodes
      return addressSuggestion.replaceAll(' ', '&nbsp;');
    },

    handleClickOutside(event) {
      if (this.closeOnBlur && !event.target?.closest?.('.form-field')?.contains(this.$el)) {
        this.close();
      }
    },

    onAutoComplete() {
      this.validationProvider?.validate();
    },
  },
};
</script>

<style lang="scss" scoped>
.auto-complete {
  position: absolute;
  top: calc(100% + math.div($pad, 4));
  left: 0;
  width: 100%;
  overflow: hidden;
  z-index: 3;

  &.auto-complete--is-full-width {
    position: fixed;
  }
}

.ac-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: ($pad * 0.25) 0 0;
  padding: ($pad * 0.5) 0;
  background: $color-general-background-neutral-default;
  border: solid 1px palette(grey, metallic);
  border-radius: map-get($border-radiuses, 'l');
  box-shadow:
    0 1px 5px 0 rgba(0, 0, 0, 0.06),
    0 4px 10px 0 rgba(0, 0, 0, 0.06);
}

.ac-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-left: $pad;

  .ac-title {
    font-size: map-get($font-sizes, 's');
    color: palette(content, secondary);
  }
}

.ac-results {
  position: relative;

  .ac-result {
    display: flex;
    padding: ($pad * 0.5) $pad;
    white-space: pre;

    &:not(.ac-result--empty) {
      cursor: pointer;

      &:hover,
      &:focus,
      &:active {
        background: palette(grey, lighter);
      }
    }
  }

  :deep(.loading-overlay .loading-icon) {
    position: relative;
  }
}
</style>
